import React, { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Receipt, AlertTriangle, DollarSign, FileDown, Printer, Mail, Link2, Ban, Pencil, RefreshCw, Send, Eye, StickyNote, Bell } from "lucide-react";
import { toast } from "sonner";

const METHODS = ["Cash", "Check", "Zelle", "ACH", "Credit card", "Debit card", "QuickBooks payment", "Other"];
const STATUSES = ["Draft", "Pending", "Sent", "Viewed", "Partially Paid", "Paid", "Void"];
const SORTS = { newest: "Newest", oldest: "Oldest", high_bal: "Highest balance", low_bal: "Lowest balance", due: "Due date", overdue: "Overdue days" };

export default function InvoiceCenter() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [dash, setDash] = useState(null);
  const [invs, setInvs] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [f, setF] = useState({ status: "", pay: "", overdue: false, client: "", state: "", qb: "", from: "", to: "" });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  const load = () => {
    api.get("/admin/invoices/dashboard").then(({ data }) => setDash(data)).catch(() => {});
    api.get("/admin/invoices").then(({ data }) => setInvs(data)).catch(() => setInvs([]));
  };
  useEffect(() => { load(); }, []);

  const clients = useMemo(() => {
    const m = {};
    (invs || []).forEach((iv) => { const n = iv.client?.company_name || `${iv.client?.first_name || ""} ${iv.client?.last_name || ""}`.trim(); if (n) m[n] = true; });
    return Object.keys(m).sort();
  }, [invs]);
  const states = useMemo(() => [...new Set((invs || []).map((iv) => iv.property?.state).filter(Boolean))].sort(), [invs]);

  const shown = useMemo(() => {
    let list = (invs || []).filter((iv) => {
      if (f.status && iv.status !== f.status) return false;
      if (f.pay === "unpaid" && !(iv.balance_due > 0 && iv.status !== "Void")) return false;
      if (f.pay === "paid" && iv.status !== "Paid") return false;
      if (f.pay === "partial" && iv.status !== "Partially Paid") return false;
      if (f.overdue && !iv.is_overdue) return false;
      if (f.client) { const n = iv.client?.company_name || `${iv.client?.first_name || ""} ${iv.client?.last_name || ""}`.trim(); if (n !== f.client) return false; }
      if (f.state && iv.property?.state !== f.state) return false;
      if (f.qb && iv.qb_sync_status !== f.qb) return false;
      if (f.from && (iv.created_at || "").slice(0, 10) < f.from) return false;
      if (f.to && (iv.created_at || "").slice(0, 10) > f.to) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${iv.invoice_number} ${iv.work_order?.wo_number || ""} ${iv.client?.company_name || ""} ${iv.client?.first_name || ""} ${iv.client?.last_name || ""} ${iv.property?.name || ""} ${iv.client?.email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const s = {
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      high_bal: (a, b) => (b.balance_due || 0) - (a.balance_due || 0),
      low_bal: (a, b) => (a.balance_due || 0) - (b.balance_due || 0),
      due: (a, b) => (a.due_date || "").localeCompare(b.due_date || ""),
      overdue: (a, b) => (b.days_overdue || 0) - (a.days_overdue || 0),
    }[sort];
    return [...list].sort(s);
  }, [invs, f, search, sort]);

  return (
    <DashboardLayout nav={getNav(user.role)} title="Invoices & Payments">
      {/* KPI row */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="invoice-kpis">
          <Kpi label="Total Invoiced" value={money(dash.total_invoiced)} />
          <Kpi label="Collected" value={money(dash.total_collected)} tone="green" />
          <Kpi label="Outstanding" value={money(dash.outstanding_balance)} tone="amber" />
          <Kpi label="Overdue" value={money(dash.overdue_balance)} tone="rose" />
          <Kpi label="Partial Balance" value={money(dash.partial_balance)} />
          <Kpi label="Unpaid / Overdue / Paid" value={`${dash.unpaid_count} / ${dash.overdue_count} / ${dash.paid_count}`} />
          <Kpi label="Avg Days to Payment" value={`${dash.avg_days_to_payment} d`} />
          <Kpi label="Total Invoices" value={dash.total_invoices} />
        </div>
      )}
      {dash && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="status-cards">
          {Object.entries(dash.status_cards).map(([k, v]) => (
            <div key={k} className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" data-testid={`status-card-${k.toLowerCase().replace(/ /g, "-")}`}>
              <span className="text-slate-500">{k}</span> <span className="font-semibold text-slate-900">{v}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-2 items-end">
        <Input data-testid="invoice-search" placeholder="Search invoice, WO, client, property, email" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full sm:w-64" />
        <FSel testid="filter-status" val={f.status} on={(v) => setF({ ...f, status: v })} ph="Status" opts={STATUSES} />
        <FSel testid="filter-pay" val={f.pay} on={(v) => setF({ ...f, pay: v })} ph="Payment" opts={[["unpaid", "Unpaid"], ["partial", "Partial"], ["paid", "Paid"]]} />
        <FSel testid="filter-client" val={f.client} on={(v) => setF({ ...f, client: v })} ph="Client" opts={clients} />
        <FSel testid="filter-state" val={f.state} on={(v) => setF({ ...f, state: v })} ph="State" opts={states} />
        <FSel testid="filter-qb" val={f.qb} on={(v) => setF({ ...f, qb: v })} ph="QB Sync" opts={["Synced", "Queued", "Failed", "Needs Review", "Not Synced"]} />
        <div className="flex items-center gap-1.5"><Input data-testid="filter-from" type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="w-36" /><span className="text-slate-400">–</span><Input data-testid="filter-to" type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="w-36" /></div>
        <label className="flex items-center gap-1.5 text-sm text-slate-600"><Checkbox checked={f.overdue} onCheckedChange={(v) => setF({ ...f, overdue: !!v })} data-testid="filter-overdue" /> Overdue only</label>
        <Select value={sort} onValueChange={setSort}><SelectTrigger data-testid="sort-select" className="w-40"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
      </div>

      {/* List */}
      {!invs ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        shown.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-slate-200"><Receipt className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No invoices match.</p></div> : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="invoice-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-200">
                {["Invoice", "WO", "Client", "Property", "Due", "Total", "Paid", "Balance", "Status", "QB Sync"].map((h) => <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {shown.map((iv) => (
                  <tr key={iv.id} data-testid={`invoice-row-${iv.id}`} onClick={() => setActiveId(iv.id)} className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer">
                    <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{iv.invoice_number}</td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{iv.work_order?.wo_number || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{iv.client?.company_name || `${iv.client?.first_name || ""} ${iv.client?.last_name || ""}`}</td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{iv.property?.name}{iv.property?.state ? `, ${iv.property.state}` : ""}</td>
                    <td className={`px-3 py-2 whitespace-nowrap ${iv.is_overdue ? "text-rose-600 font-medium" : "text-slate-500"}`}>{iv.due_date || "—"}{iv.is_overdue ? ` (${iv.days_overdue}d)` : ""}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{money(iv.total)}</td>
                    <td className="px-3 py-2 text-green-600 whitespace-nowrap">{money(iv.amount_paid)}</td>
                    <td className="px-3 py-2 font-semibold whitespace-nowrap">{money(iv.balance_due)}</td>
                    <td className="px-3 py-2"><div className="flex items-center gap-1"><StatusBadge status={iv.status} />{iv.is_overdue && <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-rose-100 text-rose-700" data-testid={`overdue-chip-${iv.id}`}>Overdue</span>}</div></td>
                    <td className="px-3 py-2"><QbChip s={iv.qb_sync_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {activeId && <InvoiceDetail id={activeId} isAdmin={isAdmin} onClose={() => setActiveId(null)} onChanged={load} />}
    </DashboardLayout>
  );
}

const Kpi = ({ label, value, tone }) => {
  const c = { green: "text-green-600", amber: "text-amber-600", rose: "text-rose-600" }[tone] || "text-slate-900";
  return <div className="bg-white border border-slate-200 rounded-xl p-3"><div className="text-xs text-slate-400">{label}</div><div className={`font-head text-lg font-semibold ${c}`}>{value}</div></div>;
};
const QbChip = ({ s }) => {
  const c = { Synced: "bg-green-100 text-green-700", Queued: "bg-slate-100 text-slate-600", Failed: "bg-rose-100 text-rose-700", "Needs Review": "bg-amber-100 text-amber-700", "Not Synced": "bg-slate-100 text-slate-500" }[s] || "bg-slate-100 text-slate-500";
  return <span className={`px-2 py-0.5 rounded-full text-xs ${c}`}>{s}</span>;
};
const FSel = ({ testid, val, on, ph, opts }) => (
  <Select value={val || "all"} onValueChange={(v) => on(v === "all" ? "" : v)}>
    <SelectTrigger data-testid={testid} className="w-40"><SelectValue placeholder={ph} /></SelectTrigger>
    <SelectContent className="max-h-64"><SelectItem value="all">All {ph}</SelectItem>
      {opts.map((o) => Array.isArray(o) ? <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem> : <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);
const Row = ({ l, v, strong }) => <div className={`flex justify-between ${strong ? "font-semibold text-slate-900" : "text-slate-600"}`}><span>{l}</span><span>{v}</span></div>;

function InvoiceDetail({ id, isAdmin, onClose, onChanged }) {
  const [iv, setIv] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const load = () => api.get(`/admin/invoices/${id}`).then(({ data }) => setIv(data)).catch(() => onClose());
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const act = async (fn, okMsg) => { try { const r = await fn(); toast.success(okMsg); load(); onChanged(); return r; } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } };
  const copyLink = async () => {
    try {
      const { data } = await api.post(`/admin/invoices/${id}/payment-link`, { action: "generate" });
      await navigator.clipboard?.writeText(`${window.location.origin}${data.url}`);
      toast.success("Secure payment link copied");
    } catch { toast.error("Could not create payment link"); }
  };
  const emailLink = () => act(() => api.post(`/admin/invoices/${id}/email-link`), "Payment link email queued");
  const refund = async (p) => {
    const reason = window.prompt(`Refund reason for payment ${money(p.amount)}?`);
    if (!reason) return;
    const amtStr = window.prompt(`Refund amount (max ${money(p.amount)})`, String(p.amount));
    if (amtStr === null) return;
    await act(() => api.post(`/invoices/${id}/payments/${p.id}/refund`, { amount: Number(amtStr), reason }), "Refund processed");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="invoice-detail-modal">
        {!iv ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2 flex-wrap">SJ Property Care — Invoice {iv.invoice_number} <StatusBadge status={iv.status} />{iv.is_overdue && <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-rose-100 text-rose-700">Overdue {iv.days_overdue}d</span>}</DialogTitle>
              <DialogDescription>SJ Contract Solutions LLC · WO {iv.work_order?.wo_number || "—"}</DialogDescription>
            </DialogHeader>

            {iv.status === "Void" && <div className="text-sm bg-slate-100 border border-slate-200 rounded-lg p-3" data-testid="void-banner">Voided by {iv.voided_by_name} on {(iv.voided_at || "").slice(0, 10)} — Reason: {iv.void_reason}</div>}

            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-0.5">
                <div className="text-xs uppercase font-bold text-slate-400">Bill To</div>
                <div className="text-slate-800">{iv.client?.company_name}</div>
                <div className="text-slate-600">{iv.client?.first_name} {iv.client?.last_name}</div>
                <div className="text-slate-500">{iv.client?.email}</div>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs uppercase font-bold text-slate-400">Service Address</div>
                <div className="text-slate-800">{iv.property?.name}</div>
                <div className="text-slate-600">{iv.property?.street_address}, {iv.property?.city}, {iv.property?.state}</div>
                <div className="text-slate-500">Invoice {(iv.created_at || "").slice(0, 10)} · Due {iv.due_date || "—"}</div>
              </div>
            </div>

            <div className="text-sm text-slate-700">{iv.description}</div>
            <div className="space-y-1 text-sm border-t pt-3">
              <Row l="Labor" v={money(iv.labor)} />
              <Row l="Materials" v={money(iv.materials)} />
              {iv.additional_charges > 0 && <Row l="Additional Charges" v={money(iv.additional_charges)} />}
              {iv.tax > 0 && <Row l="Tax" v={money(iv.tax)} />}
              {iv.discount > 0 && <Row l="Discount" v={`- ${money(iv.discount)}`} />}
              <div className="flex justify-between pt-2 border-t font-head font-semibold text-lg"><span>Total</span><span>{money(iv.total)}</span></div>
              <Row l="Amount Paid" v={money(iv.amount_paid)} />
              <div className="flex justify-between font-semibold text-rose-600"><span>Balance Due</span><span>{money(iv.balance_due)}</span></div>
            </div>

            {/* QB + meta */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-slate-50 rounded-lg p-3">
              <div><span className="text-slate-400">QB Sync: </span><QbChip s={iv.qb_sync_status} /></div>
              <div><span className="text-slate-400">QB Invoice ID: </span>{iv.quickbooks_invoice_id || "—"}</div>
              <div><span className="text-slate-400">QB Customer ID: </span>{iv.quickbooks_customer_id || "—"}</div>
              <div><span className="text-slate-400">Last Reminder: </span>{(iv.last_reminder_at || "").slice(0, 10) || "—"} {iv.reminder_count ? `(${iv.reminder_count})` : ""}</div>
              {iv.qb_last_error && <div className="col-span-2 text-rose-600">QB Error: {iv.qb_last_error}</div>}
            </div>

            {/* Payment history */}
            {iv.payments?.length > 0 && (
              <div>
                <div className="text-xs uppercase font-bold text-slate-400 mb-1">Payment History</div>
                <div className="space-y-1" data-testid="payment-history">
                  {iv.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-1.5">
                      <span>{p.date} · {p.method}{p.reference ? ` · ${p.reference}` : ""}</span>
                      <span className="flex items-center gap-2"><b className="text-green-600">{money(p.amount)}</b>
                        {p.reversed && <span className="text-xs text-rose-600">refunded</span>}
                        <button data-testid={`receipt-${p.id}`} className="text-blue-600 text-xs underline" onClick={() => openPdf(`/invoices/${iv.id}/receipt/${p.id}/pdf`)}>Receipt</button>
                        {isAdmin && !p.reversed && <button data-testid={`refund-${p.id}`} className="text-rose-600 text-xs underline" onClick={() => refund(p)}>Refund</button>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 border-t pt-3">
              <ActBtn testid="download-pdf" icon={FileDown} onClick={() => openPdf(`/invoices/${iv.id}/pdf`)}>Download PDF</ActBtn>
              <ActBtn testid="print-invoice" icon={Printer} onClick={() => openPdf(`/invoices/${iv.id}/pdf`)}>Print</ActBtn>
              <ActBtn testid="email-invoice" icon={Mail} onClick={() => act(() => api.post(`/invoices/${iv.id}/reminder`, { channel: "email" }), "Invoice email queued")}>Email</ActBtn>
              <ActBtn testid="copy-link" icon={Link2} onClick={copyLink}>Copy Payment Link</ActBtn>
              <ActBtn testid="email-link" icon={Mail} onClick={emailLink}>Email Payment Link</ActBtn>
              {iv.status !== "Void" && iv.balance_due > 0 && <ActBtn testid="record-payment-btn" icon={DollarSign} primary onClick={() => setPayOpen(true)}>Record Payment</ActBtn>}
              {isAdmin && iv.status !== "Void" && <ActBtn testid="edit-invoice-btn" icon={Pencil} onClick={() => setEditOpen(true)}>Edit</ActBtn>}
              {isAdmin && iv.status !== "Void" && <ActBtn testid="void-invoice-btn" icon={Ban} onClick={() => setVoidOpen(true)}>Void</ActBtn>}
              {isAdmin && <ActBtn testid="retry-qb-btn" icon={RefreshCw} onClick={() => act(() => api.post(`/invoices/${iv.id}/qb-sync`), "QuickBooks sync attempted")}>Retry QB Sync</ActBtn>}
              {["Draft", "Pending"].includes(iv.status) && <ActBtn testid="mark-sent-btn" icon={Send} onClick={() => act(() => api.post(`/invoices/${iv.id}/mark`, { action: "sent" }), "Marked as Sent")}>Mark Sent</ActBtn>}
              {["Sent"].includes(iv.status) && <ActBtn testid="mark-viewed-btn" icon={Eye} onClick={() => act(() => api.post(`/invoices/${iv.id}/mark`, { action: "viewed" }), "Marked as Viewed")}>Mark Viewed</ActBtn>}
              {iv.is_overdue && <ActBtn testid="send-reminder-btn" icon={Bell} onClick={() => act(() => api.post(`/invoices/${iv.id}/reminder`, { channel: "email" }), "Reminder queued")}>Send Reminder</ActBtn>}
            </div>
          </>
        )}
      </DialogContent>
      {iv && payOpen && <RecordPaymentDialog iv={iv} isAdmin={isAdmin} onClose={() => setPayOpen(false)} onDone={() => { setPayOpen(false); load(); onChanged(); }} />}
      {iv && editOpen && <EditInvoiceDialog iv={iv} onClose={() => setEditOpen(false)} onDone={() => { setEditOpen(false); load(); onChanged(); }} />}
      {iv && voidOpen && <VoidDialog iv={iv} onClose={() => setVoidOpen(false)} onDone={() => { setVoidOpen(false); load(); onChanged(); }} />}
    </Dialog>
  );
}

const ActBtn = ({ testid, icon: Icon, children, onClick, primary }) => (
  <Button data-testid={testid} size="sm" variant={primary ? "default" : "outline"} onClick={onClick} className={primary ? "bg-blue-600 hover:bg-blue-700" : ""}>
    <Icon className="w-3.5 h-3.5 mr-1" /> {children}
  </Button>
);

function RecordPaymentDialog({ iv, isAdmin, onClose, onDone }) {
  const [amount, setAmount] = useState(iv.balance_due || "");
  const [method, setMethod] = useState("Check");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [sendReceipt, setSendReceipt] = useState(false);
  const [syncQb, setSyncQb] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (confirmOver = false, confirmDup = false) => {
    if (Number(amount) < 0) return toast.error("Payment cannot be negative");
    if (!amount || Number(amount) <= 0) return toast.error("Zero-dollar payments are not allowed");
    setBusy(true);
    try {
      await api.post(`/invoices/${iv.id}/payments`, {
        amount: Number(amount), method, date, reference: reference || null, notes: notes || null,
        send_receipt: sendReceipt, sync_to_qb: syncQb, confirm_overpayment: confirmOver, confirm_duplicate: confirmDup,
      });
      toast.success("Payment recorded successfully.");
      onDone();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to record payment";
      if (e.response?.status === 409 && detail.includes("exceeds balance")) {
        if (!isAdmin) return toast.error("Only an admin can record an overpayment");
        if (window.confirm(detail + "\n\nRecord overpayment?")) return submit(true, confirmDup);
      } else if (e.response?.status === 409 && detail.includes("reference")) {
        if (window.confirm(detail)) return submit(confirmOver, true);
      } else toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="record-payment-modal">
        <DialogHeader><DialogTitle>Record Payment — {iv.invoice_number}</DialogTitle><DialogDescription>Balance due {money(iv.balance_due)}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount</Label><Input data-testid="payment-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Payment Date</Label><Input data-testid="payment-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Method</Label><Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="payment-method" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Reference #</Label><Input data-testid="payment-reference" value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Internal Notes</Label><Textarea data-testid="payment-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={sendReceipt} onCheckedChange={(v) => setSendReceipt(!!v)} data-testid="send-receipt" /> Send receipt to client</label>
          <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={syncQb} onCheckedChange={(v) => setSyncQb(!!v)} data-testid="sync-qb" /> Sync payment to QuickBooks</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="payment-submit" disabled={busy} onClick={() => submit()} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record Payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditInvoiceDialog({ iv, onClose, onDone }) {
  const [g, setG] = useState({
    description: iv.description || "", labor: iv.labor || 0, materials: iv.materials || 0,
    additional_charges: iv.additional_charges || 0, tax: iv.tax || 0, discount: iv.discount || 0,
    due_date: iv.due_date || "", notes: iv.notes || "", payment_terms: iv.payment_terms || "",
    deposit_amount: iv.deposit_amount || 0,
  });
  const [busy, setBusy] = useState(false);
  const total = (Number(g.labor) || 0) + (Number(g.materials) || 0) + (Number(g.additional_charges) || 0) + (Number(g.tax) || 0) - (Number(g.discount) || 0);
  const synced = iv.qb_sync_status === "Synced";
  const hasPayments = (iv.amount_paid || 0) > 0;
  const set = (k) => (e) => setG({ ...g, [k]: e.target.value });

  const submit = async (confirmTotals = false) => {
    setBusy(true);
    try {
      const payload = { ...g, labor: Number(g.labor), materials: Number(g.materials), additional_charges: Number(g.additional_charges), tax: Number(g.tax), discount: Number(g.discount), deposit_amount: Number(g.deposit_amount) || 0, confirm_totals: confirmTotals };
      const { data } = await api.put(`/admin/invoices/${iv.id}`, payload);
      toast.success("Invoice updated" + (data.quickbooks_note ? " — QB update may be required" : ""));
      onDone();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed";
      if (e.response?.status === 409 && detail.includes("payments")) { if (window.confirm(detail)) return submit(true); }
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-invoice-modal">
        <DialogHeader><DialogTitle>Edit Invoice {iv.invoice_number}</DialogTitle><DialogDescription>Invoice number and QuickBooks IDs are preserved.</DialogDescription></DialogHeader>
        {synced && <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2.5 flex gap-2" data-testid="qb-synced-warning"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />This invoice is already synced to QuickBooks. Changes may require a QuickBooks update.</div>}
        {hasPayments && <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-2.5">This invoice has payments — changing totals requires confirmation.</div>}
        <div className="space-y-3">
          <div><Label>Description</Label><Textarea data-testid="edit-inv-desc" value={g.description} onChange={set("description")} rows={2} className="mt-1.5" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Labor</Label><Input data-testid="edit-inv-labor" type="number" value={g.labor} onChange={set("labor")} className="mt-1.5" /></div>
            <div><Label>Materials</Label><Input data-testid="edit-inv-materials" type="number" value={g.materials} onChange={set("materials")} className="mt-1.5" /></div>
            <div><Label>Add'l</Label><Input data-testid="edit-inv-add" type="number" value={g.additional_charges} onChange={set("additional_charges")} className="mt-1.5" /></div>
            <div><Label>Tax</Label><Input data-testid="edit-inv-tax" type="number" value={g.tax} onChange={set("tax")} className="mt-1.5" /></div>
            <div><Label>Discount</Label><Input data-testid="edit-inv-discount" type="number" value={g.discount} onChange={set("discount")} className="mt-1.5" /></div>
            <div><Label>Due Date</Label><Input data-testid="edit-inv-due" type="date" value={g.due_date} onChange={set("due_date")} className="mt-1.5" /></div>
          </div>
          <div className="text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid="edit-inv-total">New Total: <b>{money(total)}</b></div>
          <div><Label>Authorized Deposit Amount (optional)</Label><Input data-testid="edit-inv-deposit" type="number" value={g.deposit_amount} onChange={set("deposit_amount")} className="mt-1.5" placeholder="0.00" /><p className="text-xs text-slate-400 mt-1">If set, the client can pay this deposit online instead of the full balance.</p></div>
          <div><Label>Payment Terms</Label><Input data-testid="edit-inv-terms" value={g.payment_terms} onChange={set("payment_terms")} className="mt-1.5" /></div>
          <div><Label>Notes</Label><Textarea data-testid="edit-inv-notes" value={g.notes} onChange={set("notes")} rows={2} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="edit-inv-submit" disabled={busy} onClick={() => submit()} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoidDialog({ iv, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Void reason is required");
    setBusy(true);
    try { await api.post(`/invoices/${iv.id}/void`, { reason }); toast.success("Invoice voided"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="void-invoice-modal">
        <DialogHeader><DialogTitle>Void Invoice {iv.invoice_number}</DialogTitle><DialogDescription>The invoice record and payment history are preserved. No new payments can be recorded.</DialogDescription></DialogHeader>
        <div><Label>Reason</Label><Textarea data-testid="void-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1.5" placeholder="Why is this invoice being voided?" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="void-submit" disabled={busy} onClick={submit} className="bg-rose-600 hover:bg-rose-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Void Invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
